import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  Dispatch,
  SetStateAction,
} from 'react';
import ArrCheckbox from '../../../common/arr-checkbox/ArrCheckbox';
import ArrRadio from '../../../common/arr-radio/ArrRadio';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrButton from '../../../common/arr-button/ArrButton';
import {CodingFormItem, ISelectItem} from '../../../../shared/types';
import {Box, Typography} from '@mui/material';
import ArrMultiSelectDropdown from '../../../common/arr-select-dropdown/ArrMultiSelectDropdown';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {t} from 'i18next';
import ArrModal from '../../../common/arr-modal/ArrModal';
import {ToastType} from '../../../../shared/constants';
import ArrTooltip from '../../../common/arr-tooltip/ArrTooltip';
import {safeParseNumber} from '../../../../utils/utils';
import ArrDatePicker from '../../../common/arr-date-picker/ArrDatePicker';

interface DynamicFormProps {
  fields: CodingFormItem[];
  setMessageType: (message: ToastType) => void;
  showTestButton?: boolean;
  onFieldsChange?: (updatedFields: CodingFormItem[]) => void;
  setCodingFormReady?: Dispatch<SetStateAction<boolean>>;
  disableAll?: boolean;
  onClear?: () => void;
  setError?: (error: string) => void;
}

interface LogicEvaluation {
  action: string;
  logicSatisfied: boolean;
  conditionType: 'Selected' | 'NotSelected' | null;
}

export interface DynamicFormFieldsHandle {
  handleValidate: (arrayToValidate?: CodingFormItem[]) => boolean;
}

const DynamicFormFields = forwardRef<DynamicFormFieldsHandle, DynamicFormProps>(
  (
    {
      fields,
      setMessageType,
      setCodingFormReady,
      showTestButton = true,
      onFieldsChange,
      disableAll = false,
      setError,
      onClear,
    },
    ref
  ) => {
    const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>(
      {}
    );
    const [updatedCodingForm, setUpdatedCodingForm] = useState<
      CodingFormItem[]
    >([]);
    const [radioSelections, setRadioSelections] = useState<
      Record<number, number | null>
    >({});
    const [comments, setComments] = useState<Record<number, string>>({});
    const [clearModal, setClearModal] = useState<boolean>(false);
    const [groupToClear, setGroupToClear] = useState<{
      id: number;
      type: 'CHECK' | 'RADIO' | 'CHECK_DROPDOWN' | 'RADIO_DROPDOWN';
    } | null>(null);
    const [selectedDropdownMulti, setSelectedDropdownMulti] = useState<
      Record<number, ISelectItem[]>
    >({});
    const [selectedDropdownSingle, setSelectedDropdownSingle] = useState<
      Record<number, ISelectItem | null>
    >({});
    const [fieldErrors, setFieldErrors] = useState<Record<number, string>>({});

    useEffect(() => {
      if (fields.length !== 0) {
        setUpdatedCodingForm(fields);
        setFieldErrors({});
      }
    }, [fields]);

    const updateFieldControlValue = (id: number, value: string) => {
      const updated = updatedCodingForm.map((f) =>
        f.id == id ? {...f, controlValue: value} : f
      );
      onFieldsChange?.(updated);
      setUpdatedCodingForm(updated);
    };

    const handleCheckboxChange = (id: number, value: boolean) => {
      setCheckedItems((prev) => ({...prev, [id]: value}));
      updateFieldControlValue(id, value ? 'True' : 'False');
    };

    const handleRadioChange = (groupId: number, selectedTagId: number) => {
      setRadioSelections((prev) => ({...prev, [groupId]: selectedTagId}));
      const groupField = updatedCodingForm.find((f) => f.id === groupId);
      if (!groupField) return;

      const groupSystemLabel = groupField.systemLabel;

      const groupTags =
        updatedCodingForm.filter(
          (f) =>
            f.type === 'TAG' &&
            f.tagType === 'RADIO' &&
            f.groupAssociation?.value === groupSystemLabel
        ) || [];

      const updatedFields = updatedCodingForm.map((field) => {
        const isTagInGroup = groupTags.some((tag) => tag.id === field.id);

        if (!isTagInGroup) return field;
        const newValue = field.id === selectedTagId ? 'True' : 'False';

        return {
          ...field,
          controlValue: newValue,
        };
      });

      onFieldsChange?.(updatedFields);
      setUpdatedCodingForm(updatedFields);
    };

    const handleCommentChange = (id: number, value: string) => {
      setComments((prev) => ({...prev, [id]: value}));
      updateFieldControlValue(id, value);
    };

    const handleClearConfirm = () => {
      if (!groupToClear) return;

      const {id, type} = groupToClear;

      if (type === 'RADIO') {
        setRadioSelections((prev) => ({...prev, [id]: null}));
      }

      if (type === 'RADIO_DROPDOWN') {
        setSelectedDropdownSingle((prev) => ({...prev, [id]: null}));
      }

      setClearModal(false);
      setGroupToClear(null);
      onClear?.();
    };

    useImperativeHandle(ref, () => ({
      handleValidate,
    }));

    const handleValidate = (): boolean => {
      const errors: Record<number, string> = {};
      fields.forEach((field) => {
        const {id, type, tagType, errorMessage} = field;
        const {action, logicSatisfied} = evaluateFieldLogic(field);

        // Determine if field is required based on default or logic
        const isRequired = action === 'REQUIRED';
        let isFilled = true;

        if (type === 'TAG' && tagType === 'CHECK') {
          const isLogicRequired =
            field.fieldLogicAction === 'REQUIRED' && logicSatisfied;
          const hasNoLogic = !field.fieldLogic || field.fieldLogic.length === 0;
          const isDefaultRequired =
            field.defaultAction === 'REQUIRED' &&
            (hasNoLogic || !logicSatisfied);
          const isAutoChecked = isLogicRequired || isDefaultRequired;
          isFilled = isAutoChecked || !!checkedItems[id];
        } else if (type === 'TAG' && tagType === 'RADIO') {
          isFilled = radioSelections[id] != null;
        } else if (type === 'COMMENT' || type === 'FIELD') {
          isFilled = !!comments[id]?.trim();
        } else if (type === 'RADIOGROUP') {
          const isDropdown = field.displayType === 'DROPDOWN';
          isFilled = isDropdown
            ? !!selectedDropdownSingle[field.id]
            : radioSelections[field.id] != null;
        } else if (type === 'CHECKGROUP') {
          const isDropdown = field.displayType === 'DROPDOWN';

          if (isDropdown) {
            isFilled = (selectedDropdownMulti[field.id] || []).length > 0;
          } else {
            const groupItems = fields.filter(
              (f) =>
                f.groupAssociation?.value === field.systemLabel &&
                f.type === 'TAG'
            );

            // Check if any group item is filled
            isFilled = groupItems.some((item) => {
              const itemLogic = evaluateFieldLogic(item);
              const itemLogicRequired =
                item.fieldLogicAction === 'REQUIRED' &&
                itemLogic.logicSatisfied;
              const itemDefaultRequired =
                item.defaultAction === 'REQUIRED' &&
                (!item.fieldLogic ||
                  item.fieldLogic.length === 0 ||
                  !itemLogic.logicSatisfied);
              const itemAutoChecked = itemLogicRequired || itemDefaultRequired;
              return itemAutoChecked || !!checkedItems[item.id];
            });
          }

          const {action: groupAction, logicSatisfied: groupLogicSatisfied} =
            evaluateFieldLogic(field);
          const groupRequired =
            groupAction === 'REQUIRED' ||
            (field.fieldLogicAction === 'REQUIRED' && groupLogicSatisfied);

          if (groupRequired && !isFilled) {
            errors[field.id] = errorMessage;
          } else {
            delete errors[field.id]; // clear previous error
          }

          return; // skip remaining checks for CHECKGROUP
        }
        if (type === 'FIELD') {
          if (
            !(
              field.metaDataType === 'DATE' ||
              field.metaDataType === 'DATETIME' ||
              field.metaDataType === 'TIME'
            )
          ) {
            const value = comments[id];
            if (value && value.trim() !== '') {
              const minValue = BigInt(
                field.minValue ?? Number.MIN_SAFE_INTEGER
              );
              const maxValue = BigInt(
                field.maxValue ?? Number.MAX_SAFE_INTEGER
              );
              const isInteger = field.metaDataType === 'INT';
              const isNvarChar = field.metaDataType === 'NVARCHAR';
              const maxLength = field.maxLength ?? 20;
              const splitLength = field.splitLength ?? 0;
              const parseResult = safeParseNumber(value, isInteger, maxLength);

              if (
                isInteger &&
                (!parseResult.isValid ||
                  parseResult.value! < minValue ||
                  parseResult.value! > maxValue)
              ) {
                errors[id] = 'Number too big';
                return;
              }

              //NVARCHAR Validations if maxLength is -1 and is multivalue
              if (isNvarChar && maxLength === -1 && splitLength != 0) {
                const txtArr = value.split(';');
                txtArr.forEach((element) => {
                  if (element.length > splitLength) {
                    errors[field.id] = 'Text too long';
                    return;
                  }
                });
              }
            }
          }
        }
        // Date validation
        const isDateField =
          field.metaDataType === 'DATE' ||
          field.metaDataType === 'DATETIME' ||
          field.metaDataType === 'TIME';

        if (isDateField) {
          const value = comments[id];

          // REQUIRED date
          if (isRequired && (!value || value.trim() === '')) {
            errors[id] = errorMessage || t('common.dateRequired');
            return;
          }

          // INVALID date format
          if (value) {
            const parsedDate = new Date(value);
            if (isNaN(parsedDate.getTime())) {
              errors[id] = 'Invalid DateTime';
              return;
            }
          }
        }

        if (isRequired && !isFilled) {
          errors[id] = errorMessage;
        }
      });

      setFieldErrors(errors);
      const isValid = Object.keys(errors).length === 0;
      setMessageType(isValid ? ToastType.Success : ToastType.Error);
      return isValid;
    };

    //onblur validations for NVARCHAR field
    const validateText = (field: CodingFormItem, text: string) => {
      const errors: Record<number, string> = {};
      const splitLength = field.splitLength ?? 0;
      if (splitLength != 0) {
        const txtArr = text.split(';');
        txtArr.forEach((element) => {
          if (element.length > splitLength) {
            errors[field.id] = 'Text too long';
            return;
          }
        });
      }
      setFieldErrors(errors);
    };

    // Core logic evaluation
    const evaluateFieldLogic = (field: CodingFormItem): LogicEvaluation => {
      const logic = field.fieldLogic;
      const action = field.fieldLogicAction || field.defaultAction || '';

      if (!logic || logic.length === 0) {
        return {
          action: field.defaultAction || '',
          logicSatisfied: true,
          conditionType: null,
        };
      }

      let result: boolean | null = null;
      let conditionType: 'Selected' | 'NotSelected' | null = null;

      const shouldUseStrictSelection = field.defaultAction === 'NOTSELECTABLE';

      const isFieldSelected = (
        targetField: CodingFormItem,
        opts?: {strict?: boolean}
      ): boolean => {
        const strict = !!opts?.strict;
        if (targetField.type === 'TAG' && targetField.tagType === 'CHECK') {
          const isManuallyChecked = !!checkedItems[targetField.id];

          if (strict) return isManuallyChecked;

          const logicSatisfiedInner =
            evaluateFieldLogic(targetField).logicSatisfied;
          const isLogicRequired =
            targetField.fieldLogicAction === 'REQUIRED' && logicSatisfiedInner;
          const hasNoLogic =
            !targetField.fieldLogic || targetField.fieldLogic.length === 0;
          const isDefaultRequired =
            targetField.defaultAction === 'REQUIRED' &&
            (hasNoLogic || !logicSatisfiedInner);

          return isManuallyChecked || isLogicRequired || isDefaultRequired;
        }

        if (targetField.type === 'TAG' && targetField.tagType === 'RADIO') {
          if (radioSelections[targetField.id] != null) return true;
          const parentRadioGroup = fields.find(
            (f) =>
              f.type === 'RADIOGROUP' &&
              f.systemLabel === targetField.groupAssociation?.value
          );
          if (parentRadioGroup) {
            const selectedTagId = radioSelections[parentRadioGroup.id];
            if (
              selectedTagId &&
              String(selectedTagId) === String(targetField.id)
            ) {
              return true;
            }
          }

          return false;
        }

        if (targetField.type === 'COMMENT' || targetField.type === 'FIELD') {
          return (comments[targetField.id]?.trim() || '').length > 0;
        }

        if (targetField.type === 'CHECKGROUP') {
          const isDropdown = targetField.displayType === 'DROPDOWN';
          if (isDropdown) {
            return (selectedDropdownMulti[targetField.id] || []).length > 0;
          } else {
            const groupItems = fields.filter(
              (f) =>
                f.groupAssociation?.value === targetField.systemLabel &&
                f.type === 'TAG'
            );
            return groupItems.some((item) => isFieldSelected(item, {strict}));
          }
        }

        if (targetField.type === 'RADIOGROUP') {
          const isDropdown = targetField.displayType === 'DROPDOWN';
          return isDropdown
            ? !!selectedDropdownSingle[targetField.id]
            : radioSelections[targetField.id] != null;
        }

        return false;
      };

      for (let i = 0; i < logic.length; i++) {
        const rule = logic[i];
        let targetField = fields.find((f) => f.systemLabel === rule.objectname);
        if (!targetField) {
          const nestedTag = fields.find(
            (f) =>
              f.type === 'TAG' &&
              f.groupAssociation?.value === rule.objectname &&
              f.tagType === 'RADIO'
          );
          if (nestedTag) {
            targetField = nestedTag;
          }
        }
        if (!targetField) continue;

        conditionType = rule.condition as 'Selected' | 'NotSelected';

        const selected = isFieldSelected(targetField, {
          strict: shouldUseStrictSelection,
        });
        const conditionMet =
          rule.condition === 'Selected' ? selected : !selected;

        if (i === 0) {
          result = conditionMet;
        } else {
          const operator = logic[i - 1].operator?.toUpperCase();
          result =
            operator === 'AND'
              ? result && conditionMet
              : result || conditionMet;
        }
      }

      const finalAction =
        result && action === 'REQUIRED' && field.defaultAction === 'HIDDEN'
          ? 'REQUIRED'
          : result
            ? action
            : field.defaultAction || '';

      return {
        action: finalAction,
        logicSatisfied: !!result,
        conditionType,
      };
    };

    const groupTagMap = new Map<string, CodingFormItem[]>();
    fields.forEach((field) => {
      if (field.type === 'TAG' && field.groupAssociation) {
        const key = field.groupAssociation?.value ?? '';
        if (!groupTagMap.has(key.toString()))
          groupTagMap.set(key.toString(), []);
        groupTagMap.get(key.toString())!.push(field);
      }
    });

    React.useEffect(() => {
      const updated: Record<number, boolean> = {};

      fields.forEach((field) => {
        const isUngroupedCheckbox =
          field.type === 'TAG' &&
          field.tagType === 'CHECK' &&
          (!field.groupAssociation || field.groupAssociation.value === 'false');

        if (!isUngroupedCheckbox) return;

        const {action, logicSatisfied, conditionType} =
          evaluateFieldLogic(field);
        const isRequired = action === 'REQUIRED';
        const shouldAutoCheck =
          isRequired && logicSatisfied && conditionType === 'Selected';

        if (shouldAutoCheck) {
          updated[field.id] = true;
        }
      });

      setCheckedItems((prev) => {
        const merged = {...prev};
        let changed = false;
        Object.entries(updated).forEach(([idStr, checked]) => {
          const id = parseInt(idStr);
          if (!prev[id]) {
            merged[id] = checked;
            changed = true;
          }
        });
        return changed ? merged : prev;
      });
    }, [fields, checkedItems, evaluateFieldLogic]);

    useEffect(() => {
      const updatedCheckboxes: Record<number, boolean> = {};
      const updatedRadioSelections: Record<number, number> = {};
      const updatedDropdownMulti: Record<number, ISelectItem[]> = {};
      const updatedDropdownSingle: Record<number, ISelectItem> = {};
      const updatedComments: Record<number, string> = {};
      setCodingFormReady?.(false);
      setCheckedItems({});
      setRadioSelections({});
      setComments({});
      setError?.('');
      //COUNT how many radios in each group have controlValue = "True"
      const radioTrueCount: Record<number, number> = {};
      const radioTrueLabels: Record<number, string[]> = {};
      const radioTrueIds: Record<number, number[]> = {};

      fields.forEach((field) => {
        if (
          field.type === 'TAG' &&
          field.tagType === 'RADIO' &&
          field.controlValue === 'True'
        ) {
          const parent = field.groupAssociation
            ? fields.find(
                (f) => f.systemLabel === field?.groupAssociation?.value
              )
            : null;

          const groupId = parent?.id ?? field.id;

          radioTrueCount[groupId] = (radioTrueCount[groupId] || 0) + 1;

          if (!radioTrueLabels[groupId]) radioTrueLabels[groupId] = [];
          if (!radioTrueIds[groupId]) radioTrueIds[groupId] = [];

          radioTrueLabels[groupId].push(field?.friendlyLabel ?? '');
          radioTrueIds[groupId].push(field.id);
        }
      });
      for (const groupId in radioTrueCount) {
        if (radioTrueCount[groupId] > 1) {
          const parentGroup = fields.find((f) => f.id === Number(groupId));
          const conflictLabels = radioTrueLabels[groupId].join(', ');

          setError?.(
            `This document has the following coding conflicts. Please review the document and reapply the coding.\n` +
              `${parentGroup?.friendlyLabel} has already been tagged with ${conflictLabels}.`
          );
        }
      }

      // Now we safely auto-select fields
      fields.forEach((field) => {
        const {id, type, tagType, controlValue, groupAssociation} = field;

        // Checkbox auto-select
        if (type === 'TAG' && tagType === 'CHECK' && controlValue === 'True') {
          updatedCheckboxes[id] = true;
        }

        // Radio auto-select — only if the group has exactly 1 True
        if (type === 'TAG' && tagType === 'RADIO') {
          const parent = groupAssociation
            ? fields.find((f) => f.systemLabel === groupAssociation.value)
            : null;

          const groupId = parent?.id ?? id;
          const trueCount = radioTrueCount[groupId] || 0;

          if (trueCount === 1 && controlValue === 'True') {
            updatedRadioSelections[groupId] = id;
          }
        }

        // Multi-select dropdown auto-select
        if (
          type === 'TAG' &&
          tagType === 'CHECK' &&
          groupAssociation &&
          controlValue === 'True'
        ) {
          const parentGroup = fields.find(
            (f) => f.systemLabel === groupAssociation.value
          );

          if (parentGroup && parentGroup.displayType === 'DROPDOWN') {
            const groupId = parentGroup.id;
            if (!updatedDropdownMulti[groupId])
              updatedDropdownMulti[groupId] = [];

            updatedDropdownMulti[groupId].push({
              id: field.id,
              name: field.friendlyLabel ?? field.customControlLabel ?? '',
            });
          }
        }

        // Single-select Dropdown (RADIOGROUP)
        if (
          type === 'TAG' &&
          tagType === 'RADIO' &&
          groupAssociation &&
          controlValue === 'True'
        ) {
          const parentGroup = fields.find(
            (f) => f.systemLabel === groupAssociation.value
          );

          if (parentGroup && parentGroup.displayType === 'DROPDOWN') {
            const groupId = parentGroup.id;
            updatedDropdownSingle[groupId] = {
              id: field.id,
              name: field.friendlyLabel ?? field.customControlLabel ?? '',
            };
          }
        }

        // Comments
        if (
          (type === 'COMMENT' || type === 'FIELD') &&
          typeof controlValue === 'string'
        ) {
          updatedComments[id] = controlValue;
        }
      });

      setCheckedItems((prev) => ({...prev, ...updatedCheckboxes}));
      setRadioSelections((prev) => ({...prev, ...updatedRadioSelections}));
      setSelectedDropdownMulti((prev) => ({...prev, ...updatedDropdownMulti}));
      setSelectedDropdownSingle((prev) => ({
        ...prev,
        ...updatedDropdownSingle,
      }));
      setComments((prev) => ({...prev, ...updatedComments}));
      setCodingFormReady?.(true);
    }, [fields]);

    const renderGroupWithTags = (
      group: CodingFormItem,
      items: CodingFormItem[],
      type: 'CHECK' | 'RADIO'
    ) => {
      const groupId = group.id;
      const isCheck = type === 'CHECK';

      const groupAction = evaluateFieldLogic(group);
      const groupDisabled =
        groupAction.action === 'NOTSELECTABLE' || disableAll;
      const groupRequired = groupAction.action === 'REQUIRED';

      const handleClearClick = (groupId: number, type: 'CHECK' | 'RADIO') => {
        setGroupToClear({id: groupId, type});
        setClearModal(true);
      };

      return (
        <Box key={groupId} className="group-box">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            className={`${type === 'RADIO' && 'check-group-label'}`}
          >
            <Typography
              className="typo-text label3_size field-label"
              data-testid={`dynamic-form-fields-help-text-${group.id}`}
            >
              {group.helpText}
              {groupRequired && (
                <span data-testid={`dynamic-form-fields-${group.id}`}> *</span>
              )}
            </Typography>
            {type === 'RADIO' && !disableAll && (
              <ArrButton
                onClick={() => handleClearClick(groupId, type)}
                size="small"
                variant="text"
                disabled={groupDisabled}
                dataTestId={`dynamic-form-fields-group-tags-clear-button-${groupId}`}
              >
                {t('common.clear')}
              </ArrButton>
            )}
          </Box>
          {isCheck ? (
            items.map((item, index) => {
              const itemAction = evaluateFieldLogic(item);
              if (itemAction.action === 'HIDDEN') return null;
              const required = itemAction.action === 'REQUIRED';
              const disabled =
                itemAction.action === 'NOTSELECTABLE' ||
                groupDisabled ||
                disableAll;

              return (
                <Box className="fields-list group-check-list" key={item.id}>
                  <ArrCheckbox
                    label={item.friendlyLabel}
                    checked={!!checkedItems[item.id]}
                    size="medium"
                    onChange={(val: boolean) =>
                      handleCheckboxChange(item.id, val)
                    }
                    disabled={disabled}
                    aria-label={`select-checkbox-${groupId}-${index}`}
                  />
                  <ArrTooltip
                    content={item.helpText}
                    showOnTruncate={item.helpText === ''}
                    isTruncated={false}
                    labelId={`tooltip-item-${item.id}-${index}`}
                  >
                    <Typography
                      className="typo-text body4_size field-label"
                      data-testid={`dynamic-form-fields-label-${groupId}-${index}`}
                    >
                      {item.friendlyLabel}
                      {required && (
                        <span
                          data-testid={`dynamic-form-fields--text-${groupId}-${index}`}
                        >
                          {' '}
                          *
                        </span>
                      )}
                    </Typography>
                  </ArrTooltip>
                </Box>
              );
            })
          ) : (
            <Box className="radio-field">
              <ArrRadio
                dataTestId={`radio-group-${groupId}`}
                name={`radio-group-${groupId}`}
                value={radioSelections[groupId]?.toString() ?? ''}
                onChange={(val: string) =>
                  handleRadioChange(groupId, Number(val))
                }
                data={items
                  .filter(
                    (item) => evaluateFieldLogic(item).action !== 'HIDDEN'
                  )
                  .map((item) => ({
                    value: item.id.toString(),
                    label: item.friendlyLabel ?? '',
                    tooltip: item.helpText,
                    disabled:
                      evaluateFieldLogic(item).action === 'NOTSELECTABLE' ||
                      groupDisabled ||
                      disableAll,
                  }))}
                size="xs"
                disabled={groupDisabled || disableAll}
              />
            </Box>
          )}
          {fieldErrors[groupId] && (
            <Typography
              className="typo-text body4_size text-error"
              data-testid={`dynamic-form-fields-typography-error-${groupId}`}
            >
              {fieldErrors[groupId]}
            </Typography>
          )}
        </Box>
      );
    };

    const renderGroupAsDropdown = (
      group: CodingFormItem,
      items: CodingFormItem[],
      type: 'CHECK' | 'RADIO'
    ) => {
      const groupId = group.id;
      const groupAction = evaluateFieldLogic(group);
      const groupDisabled = groupAction.action === 'NOTSELECTABLE';
      const groupRequired = groupAction.action === 'REQUIRED';

      const dropdownOptions: ISelectItem[] = items
        .filter((item) => evaluateFieldLogic(item).action !== 'HIDDEN')
        .map((item) => ({
          id: item.id,
          name: item.friendlyLabel ?? '',
          disabled:
            evaluateFieldLogic(item).action === 'NOTSELECTABLE' ||
            groupDisabled ||
            disableAll,
        }));

      const handleClearClickDropdown = (
        groupId: number,
        type: 'CHECK_DROPDOWN' | 'RADIO_DROPDOWN'
      ) => {
        setGroupToClear({id: groupId, type});
        setClearModal(true);
      };

      const handleCheckDropdownChange = (
        groupId: number,
        values: ISelectItem[]
      ) => {
        setSelectedDropdownMulti((prev) => ({
          ...prev,
          [groupId]: values,
        }));

        const selectedIds = new Set(values.map((v) => v.id));

        const updated = updatedCodingForm.map((field) => {
          if (
            field.type === 'TAG' &&
            field.tagType === 'CHECK' &&
            field.groupAssociation?.value ===
              updatedCodingForm.find((f) => f.id === groupId)?.systemLabel
          ) {
            return {
              ...field,

              controlValue: selectedIds.has(field.id) ? 'True' : 'False',
            };
          }

          return field;
        });

        setUpdatedCodingForm(updated);
        onFieldsChange?.(updated);
      };

      const handleRadioDropdownChange = (
        groupId: number,
        val: ISelectItem | null
      ) => {
        setSelectedDropdownSingle((prev) => ({
          ...prev,
          [groupId]: val,
        }));

        const updated = updatedCodingForm.map((field) => {
          if (
            field.type === 'TAG' &&
            field.tagType === 'RADIO' &&
            field.groupAssociation?.value ===
              updatedCodingForm.find((f) => f.id === groupId)?.systemLabel
          ) {
            return {
              ...field,
              controlValue: field.id === val?.id ? 'True' : 'False',
            };
          }

          return field;
        });

        setUpdatedCodingForm(updated);
        onFieldsChange?.(updated);
      };

      return (
        <Box key={groupId} className="group-box">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              className="typo-text label3_size field-label"
              data-testid={`dynamic-form-fields-group-help-text-${groupId}`}
            >
              {group.helpText}
              {groupRequired && (
                <span data-testid={`dynamic-form-required-${groupId}`}> *</span>
              )}
            </Typography>
          </Box>
          {type === 'CHECK' ? (
            <Box>
              <ArrMultiSelectDropdown
                dropdownLabel={`${group.friendlyLabel}${groupRequired ? ' *' : ''}`}
                dropdownItems={dropdownOptions}
                selectedValues={selectedDropdownMulti[groupId] || []}
                onSelectionChange={(values: ISelectItem[]) =>
                  handleCheckDropdownChange(groupId, values)
                }
                multiple
                sizeVariant="small"
                searchable
                withSelectAll={false}
                customWidth={18}
                checkboxDataTestId={`dynamic-form-fields-group-${groupId}-dropdown-checkbox`}
              />
              {fieldErrors[groupId] && (
                <Typography
                  className="typo-text body4_size text-error"
                  data-testid={`dynamic-form-fields-field-error-${groupId}`}
                >
                  {fieldErrors[groupId]}
                </Typography>
              )}
            </Box>
          ) : (
            <Box>
              <Box className="radio-group-clear">
                <ArrSingleSelectDropdown
                  dropdownLabel={`${group.friendlyLabel}${groupRequired ? ' *' : ''}`}
                  dropdownItems={dropdownOptions}
                  sizeVariant="small"
                  dropdownItemKey="id"
                  dropdownItemDisplay="name"
                  selectedValues={selectedDropdownSingle[groupId] || null}
                  onSelectionChange={(val: ISelectItem | null) =>
                    handleRadioDropdownChange(groupId, val)
                  }
                  dataTestId={`asd-dropdown-single-${groupId}`}
                  aria-label={group.friendlyLabel}
                />
                {type === 'RADIO' && !disableAll && (
                  <ArrButton
                    onClick={() =>
                      handleClearClickDropdown(groupId, 'RADIO_DROPDOWN')
                    }
                    size="small"
                    variant="text"
                    disabled={groupDisabled}
                    dataTestId={`dynamic-form-fields-clear-button-${groupId}`}
                  >
                    {t('common.clear')}
                  </ArrButton>
                )}
              </Box>
              {fieldErrors[groupId] && (
                <Typography
                  className="typo-text body4_size text-error"
                  data-testid={`dynamic-error-s-${groupId}`}
                >
                  {fieldErrors[groupId]}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      );
    };

    return (
      <>
        {fields.map((field, idx) => {
          const {
            id,
            type,
            tagType,
            friendlyLabel,
            groupAssociation,
            systemLabel,
            displayType,
            helpText,
            controlValue,
          } = field;
          const evaluatedAction = evaluateFieldLogic(field);
          if (evaluatedAction.action === 'HIDDEN') return null;

          const disabled =
            evaluatedAction.action === 'NOTSELECTABLE' || disableAll;
          const required = evaluatedAction.action === 'REQUIRED';

          if (
            type === 'TAG' &&
            tagType === 'CHECK' &&
            (!groupAssociation || groupAssociation.value === 'false')
          ) {
            const {logicSatisfied, action} = evaluateFieldLogic(field);

            const isLogicRequired =
              field.fieldLogicAction === 'REQUIRED' && logicSatisfied;
            const hasNoLogic =
              !field.fieldLogic || field.fieldLogic.length === 0;
            const isDefaultRequired =
              field.defaultAction === 'REQUIRED' &&
              (hasNoLogic || !logicSatisfied);

            const shouldAutoCheck = isLogicRequired || isDefaultRequired;
            const shouldDisable =
              disableAll || action === 'NOTSELECTABLE' || shouldAutoCheck;
            const showAsterisk = shouldAutoCheck;

            return (
              <Box key={id}>
                <Box className="fields-list field-single-check" key={id}>
                  <ArrCheckbox
                    label={friendlyLabel}
                    checked={shouldAutoCheck ? true : !!checkedItems[id]}
                    size="medium"
                    onChange={(val: boolean) => {
                      if (!shouldDisable) {
                        handleCheckboxChange(id, val);
                      }
                    }}
                    disabled={shouldDisable}
                    data-testid={friendlyLabel}
                    aria-label={`Select-field-cb-${idx}`}
                  />
                  <ArrTooltip
                    content={helpText}
                    showOnTruncate={helpText === ''}
                    isTruncated={false}
                    labelId={`tooltip-${idx}`}
                  >
                    <Typography
                      className="typo-text body4_size field-label"
                      data-testid={`dynamic-form-fields-typography-field-label-${idx}`}
                    >
                      {friendlyLabel}
                      {showAsterisk && (
                        <span data-testid={`dynamic-form-fields--button`}>
                          {' '}
                          *
                        </span>
                      )}
                    </Typography>
                  </ArrTooltip>
                </Box>
                {fieldErrors[id] && (
                  <Typography
                    className="typo-text body4_size text-error"
                    data-testid={`dynamic-form-fields-typography-field-level-error-${idx}`}
                  >
                    {fieldErrors[id]}
                  </Typography>
                )}
              </Box>
            );
          }

          if (
            type === 'TAG' &&
            tagType === 'RADIO' &&
            (!groupAssociation || groupAssociation.value === 'false')
          ) {
            return (
              <Box key={id} className="fields-list">
                <ArrTooltip
                  content={helpText}
                  showOnTruncate={helpText === ''}
                  isTruncated={false}
                  labelId={`tooltip-tag-${idx}`}
                >
                  <Typography
                    className="typo-text label3_size field-label"
                    data-testid={`dynamic-form-fields-tag-radio-label-${idx}`}
                  >
                    {field.friendlyLabel}
                  </Typography>
                </ArrTooltip>
              </Box>
            );
          }

          if (
            (type === 'CHECKGROUP' || type === 'RADIOGROUP') &&
            evaluatedAction.action !== 'HIDDEN'
          ) {
            const items = groupTagMap.get(systemLabel) || [];
            if (items.length === 0) return null;

            const isDropdown = displayType === 'DROPDOWN';
            const groupType = type === 'CHECKGROUP' ? 'CHECK' : 'RADIO';

            return isDropdown
              ? renderGroupAsDropdown(field, items, groupType)
              : renderGroupWithTags(field, items, groupType);
          }

          if (type === 'COMMENT' || type === 'FIELD') {
            const formattedValue = controlValue?.replace(/-/g, '/');
            const isDatePicker =
              field.metaDataType === 'DATETIME' ||
              field.metaDataType === 'DATE' ||
              field.metaDataType === 'TIME';
            const isInteger = field.metaDataType === 'INT';
            return (
              <Box className="group-item group-item-field" key={id}>
                {type === 'FIELD' && (
                  <Typography
                    className="typo-text body4_size field-label field-label-top"
                    data-testid={`dynamic-form-fields-checkgroup-label-${idx}`}
                  >
                    {friendlyLabel}
                    {required && (
                      <span data-testid={`dynamic-form-fields--icon`}> *</span>
                    )}
                  </Typography>
                )}
                <ArrTooltip
                  content={helpText}
                  showOnTruncate={helpText === ''}
                  isTruncated={false}
                  labelId={`tooltip-date-time-${idx}`}
                >
                  {isDatePicker ? (
                    <ArrDatePicker
                      defaultError={Boolean(fieldErrors[id])}
                      dataTestId={`field-input-${id}`}
                      onChange={(dateValue: any) => {
                        const stringValue = dateValue;
                        handleCommentChange(id, stringValue);
                      }}
                      label={friendlyLabel}
                      placeholder="YYYY/MM/DD"
                      type="date-picker"
                      initialStartDate={formattedValue}
                      inputClass="date-picker-error"
                      customError={t('common.arrDatePicker.customError')}
                      onFocusOpenDatePicker
                      disabled={disabled}
                    />
                  ) : (
                    <ArrTextField
                      sizeVariant={type === 'FIELD' ? 'small' : 'large'}
                      id={`field-${id}`}
                      dataTestId={`dynamic-form-field-input-${id}`}
                      disabled={disableAll}
                      value={comments[id] || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const inputValue = e.target.value;
                        if (type === 'FIELD' && inputValue) {
                          const maxLength = field.maxLength ?? 11;
                          if (inputValue.length > maxLength && maxLength > 0)
                            return;
                          if (isInteger) {
                            if (inputValue !== '-') {
                              const parseResult = safeParseNumber(
                                inputValue,
                                isInteger,
                                maxLength
                              );
                              if (!parseResult.isValid && inputValue !== '-')
                                return;
                            }
                          }
                        }

                        handleCommentChange(id, inputValue);
                      }}
                      state={
                        disabled
                          ? 'inactive'
                          : fieldErrors[id]
                            ? 'error'
                            : 'enabled'
                      }
                      label={
                        type === 'COMMENT' ? (
                          <>
                            {friendlyLabel}
                            {required && (
                              <span data-testid={`dynamic-form-fields--link`}>
                                {' '}
                                *
                              </span>
                            )}
                          </>
                        ) : null
                      }
                      multiline={type === 'COMMENT'}
                      fullWidth
                      supportText={fieldErrors[id] || ''}
                      error={Boolean(fieldErrors[id])}
                      onBlur={(
                        e: React.FocusEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >
                      ) => {
                        if (field.metaDataType === 'NVARCHAR') {
                          const textValue = e.target.value;
                          const maxLength = field.maxLength ?? 20;

                          if (textValue === '') {
                            return true;
                          } else {
                            if (
                              maxLength > -1 &&
                              textValue.length <= maxLength
                            ) {
                              return true;
                            } else if (maxLength === -1) {
                              validateText(field, textValue);
                            }
                          }
                        }
                      }}
                    />
                  )}
                </ArrTooltip>
              </Box>
            );
          }

          if (type === 'STATICTEXT') {
            return (
              <Box className="fields-list" key={id}>
                <Typography
                  className="typo-text label3_size field-label"
                  data-testid={`dynamic-form-statisc-label-${idx}`}
                >
                  {friendlyLabel}
                </Typography>
              </Box>
            );
          }

          return null;
        })}
        {showTestButton && (
          <div data-testid={`dynamic-form-fields-test-button`}>
            <ArrButton
              variant="secondary"
              size="medium"
              onClick={handleValidate}
              dataTestId="test-button"
              className="test-button"
            >
              {t('codingForms.previewCodingForm.testButton')}
            </ArrButton>
          </div>
        )}
        {clearModal && (
          <ArrModal
            showModal={clearModal}
            onClose={() => {
              setClearModal(false);
              setGroupToClear(null);
            }}
            title={t('common.clear')}
            description={t('codingForms.previewCodingForm.clearDescription')}
            buttons={[
              {
                id: '1',
                label: t('common.no'),
                onClick: () => {
                  setClearModal(false);
                  setGroupToClear(null);
                },
                variant: 'secondary',
                dataTestId: 'dff-no-btn',
              },
              {
                id: '2',
                label: t('common.yes'),
                onClick: handleClearConfirm,
                variant: 'primary',
                dataTestId: 'dff-yes-btn',
              },
            ]}
            dataTestId="clear-group-modal"
            disablePortal={true} // ✅ Only enable for this specific modal
          />
        )}
      </>
    );
  }
);

export default DynamicFormFields;
